import type { Currency, NavMonthlyPoint } from '@nav/shared';

import { Card } from '@/components/ui/Card';
import { EmptyState, ErrorState, LoadingRegion, Skeleton } from '@/components/ui/states';
import { EM_DASH, formatNav, formatPctPlain } from '@/lib/format';
import { BRAND } from '../palette';

/**
 * “Data Lengkap” — one row per period: NAV, MoM %, 3 Bulan %, YoY %.
 * The latest row is tinted with the brand-red accent. MoM/3M/YoY cells always
 * use the green/red trend colour, on every row including the latest — those
 * are signal, not brand chrome.
 */

function pctColor(value: number | null): string {
  if (value == null) return BRAND.textFaint;
  if (value > 0) return BRAND.mint;
  if (value < 0) return BRAND.coral;
  return BRAND.ink;
}

export function NavMonthlyTable({
  points,
  currency,
  sourceNote,
  isLoading,
  error,
  onRetry,
}: {
  points: NavMonthlyPoint[];
  currency: Currency;
  sourceNote?: string;
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
}) {
  if (isLoading) {
    return (
      <Card>
        <LoadingRegion label="Memuat tabel NAV bulanan" className="p-7">
          <Skeleton height={34} radius={6} />
          <div style={{ height: 8 }} />
          <Skeleton height={34} radius={6} />
          <div style={{ height: 8 }} />
          <Skeleton height={34} radius={6} />
        </LoadingRegion>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <ErrorState error={error} onRetry={onRetry} compact />
      </Card>
    );
  }

  if (points.length === 0) {
    return (
      <Card>
        <EmptyState message="Belum ada data NAV untuk produk ini." compact />
      </Card>
    );
  }

  const lastIndex = points.length - 1;

  return (
    <Card>
      <div className="overflow-x-auto no-scrollbar px-3 py-2">
        <table
          className="w-full border-collapse"
          style={{ fontFamily: 'var(--font-sans)', fontSize: 13 }}
        >
          <caption className="sr-only">NAV akhir bulan, pertumbuhan bulanan, 3 bulanan, dan year-on-year</caption>
          <thead>
            <tr>
              {['Periode', 'NAV', '% Growth Monthly', '3 Bulan %', 'YOY'].map((h, i) => (
                <th
                  key={h}
                  scope="col"
                  style={{
                    textAlign: i === 0 ? 'left' : 'right',
                    padding: '10px 12px',
                    color: BRAND.textFaint,
                    fontWeight: 500,
                    fontSize: 11,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    borderBottom: `1px solid ${BRAND.panelBorder}`,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {points.map((point, i) => {
              const isLatest = i === lastIndex;
              const rowColor = isLatest ? BRAND.red : BRAND.ink;
              return (
                <tr
                  key={point.period}
                  style={{
                    background: isLatest ? BRAND.redTint : undefined,
                    fontWeight: isLatest ? 600 : 400,
                  }}
                >
                  <th
                    scope="row"
                    style={{
                      textAlign: 'left',
                      padding: '11px 12px',
                      color: isLatest ? BRAND.red : BRAND.textDim,
                      fontWeight: isLatest ? 600 : 400,
                      fontSize: 12.5,
                      borderBottom: '1px solid rgba(27,36,54,0.07)',
                    }}
                  >
                    {point.label}
                    {point.isInception ? (
                      <span className="ml-2 text-[9px] uppercase tracking-wide" style={{ color: BRAND.mint }}>
                        Inception
                      </span>
                    ) : null}
                    {point.isSalesStart ? (
                      <span className="ml-2 text-[9px] uppercase tracking-wide" style={{ color: BRAND.charcoal }}>
                        Mulai jual
                      </span>
                    ) : null}
                  </th>
                  <td
                    className="tabular-nums"
                    style={{
                      textAlign: 'right',
                      padding: '11px 12px',
                      color: rowColor,
                      borderBottom: '1px solid rgba(27,36,54,0.07)',
                    }}
                  >
                    {formatNav(point.navClose, currency)}
                  </td>
                  <td
                    className="tabular-nums"
                    style={{
                      textAlign: 'right',
                      padding: '11px 12px',
                      color: pctColor(point.momPct),
                      borderBottom: '1px solid rgba(27,36,54,0.07)',
                    }}
                  >
                    {point.momPct == null ? EM_DASH : formatPctPlain(point.momPct)}
                  </td>
                  <td
                    className="tabular-nums"
                    style={{
                      textAlign: 'right',
                      padding: '11px 12px',
                      color: pctColor(point.threeMonthPct),
                      borderBottom: '1px solid rgba(27,36,54,0.07)',
                    }}
                  >
                    {point.threeMonthPct == null ? EM_DASH : formatPctPlain(point.threeMonthPct)}
                  </td>
                  <td
                    className="tabular-nums"
                    style={{
                      textAlign: 'right',
                      padding: '11px 12px',
                      color: pctColor(point.yoyPct),
                      borderBottom: '1px solid rgba(27,36,54,0.07)',
                    }}
                  >
                    {point.yoyPct == null ? EM_DASH : formatPctPlain(point.yoyPct)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {sourceNote ? (
        <p
          className="px-5 pb-5 pt-1"
          style={{ fontFamily: 'var(--font-sans)', fontSize: 11, color: BRAND.textFaint, lineHeight: 1.6 }}
        >
          {sourceNote}
        </p>
      ) : null}
    </Card>
  );
}
